import React, { useState } from "react";
import { resetClientCreditGrace, updateClient } from "../api";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-CL");
};

const ClientCreditModal = ({ client, canManageCredit, onClose, onSaved, canUpdateClient }) => {
  const [creditLimit, setCreditLimit] = useState(
    client?.creditLimit !== null && client?.creditLimit !== undefined
      ? String(client.creditLimit)
      : ""
  );
  const [creditBlocked, setCreditBlocked] = useState(Boolean(client?.creditBlocked));
  const [creditWarningNote, setCreditWarningNote] = useState(client?.creditWarningNote || "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);

  if (!client) return null;

  const handleSave = async () => {
    if (!canUpdateClient) return;
    setError("");
    const trimmedLimit = creditLimit.trim();
    const parsedLimit = trimmedLimit === "" ? null : Number(trimmedLimit);
    if (parsedLimit !== null && (!Number.isFinite(parsedLimit) || parsedLimit < 0)) {
      setError("El limite debe ser un numero valido.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        creditLimit: parsedLimit,
        creditBlocked,
        creditWarningNote: creditWarningNote.trim() || null,
      };
      await updateClient(client.id, payload);
      if (onSaved) {
        await onSaved(payload);
      }
      onClose();
    } catch (err) {
      console.error(err);
      setError("No se pudo actualizar el credito.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetGrace = async () => {
    if (!canUpdateClient) return;
    setResetting(true);
    try {
      const response = await resetClientCreditGrace(client.id);
      if (onSaved) {
        await onSaved({ creditLimitGraceUsedAt: null });
      }
      if (response?.client?.creditLimitGraceUsedAt === null) {
        setError("");
      }
    } catch (err) {
      console.error(err);
      setError("No se pudo resetear la gracia.");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow max-w-lg w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-2 text-gray-600 hover:text-black"
          onClick={onClose}
          type="button"
        >
          x
        </button>
        <h2 className="text-xl font-bold mb-4">Credito cliente</h2>
        <div className="space-y-3">
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <input
            type="number"
            min="0"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            className="w-full border border-gray-300 rounded p-2"
            placeholder="Limite de credito (opcional)"
            disabled={!canUpdateClient}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={creditBlocked}
              onChange={(e) => setCreditBlocked(e.target.checked)}
              disabled={!canUpdateClient}
            />
            Bloquear credito para este cliente
          </label>
          <textarea
            value={creditWarningNote}
            onChange={(e) => setCreditWarningNote(e.target.value)}
            className="w-full border border-gray-300 rounded p-2"
            rows={3}
            placeholder="Nota o advertencia"
            disabled={!canUpdateClient}
          />
          {client.creditLimitGraceUsedAt && (
            <div className="text-xs text-gray-600">
              Gracia usada: {formatDateTime(client.creditLimitGraceUsedAt)}
            </div>
          )}
          {canManageCredit && client.creditLimitGraceUsedAt && canUpdateClient && (
            <button
              onClick={handleResetGrace}
              disabled={resetting}
              className="px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-60"
              type="button"
            >
              Resetear gracia
            </button>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            type="button"
          >
            Cancelar
          </button>
          {canUpdateClient && (
            <button
              onClick={handleSave}
              disabled={submitting}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-60"
              type="button"
            >
              Guardar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCreditModal;
